import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileUp, Loader2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import DocumentPreview from "@/components/application/DocumentPreview";

interface RTWSectionProps {
  rtwShareCode: string;
  setRtwShareCode: (value: string) => void;
  documentFile: File | null;
  setDocumentFile: (file: File | null) => void;
  documentPreview: string | null;
  setDocumentPreview: (preview: string | null) => void;
  onSubmit: () => void;
  isLoading?: boolean;
}

const RTWSection: React.FC<RTWSectionProps> = ({
  rtwShareCode,
  setRtwShareCode,
  documentFile,
  setDocumentFile,
  documentPreview,
  setDocumentPreview,
  onSubmit,
  isLoading = false,
}) => {
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setDocumentFile(file);
      const fileUrl = URL.createObjectURL(file);
      setDocumentPreview(fileUrl);
    }
  };

  const triggerFileInput = (inputId: string) => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      input.click();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!rtwShareCode && !documentFile && !documentPreview) {
      toast({
        title: "Missing Information",
        description:
          "Please provide either your Right to Work share code OR upload your passport/birth certificate",
        variant: "destructive",
      });
      return;
    }

    if (rtwShareCode) {
      // Check if exactly 9 characters
      if (rtwShareCode.length !== 9) {
        toast({
          title: "Invalid Right to Work Share Code",
          description:
            "Right to Work share code must be exactly 9 characters long",
          variant: "destructive",
        });
        return;
      }

      // Check if alphanumeric (letters and numbers only)
      if (!/^[a-zA-Z0-9]+$/.test(rtwShareCode)) {
        toast({
          title: "Invalid Right to Work Share Code",
          description:
            "Right to Work share code must contain only letters and numbers",
          variant: "destructive",
        });
        return;
      }
    }

    onSubmit();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Right to Work Check</CardTitle>
        <CardDescription>
          Please provide your Right to Work share code OR upload your
          passport/birth certificate
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="rtwShareCode">Right to Work Share Code</Label>
            <Input
              id="rtwShareCode"
              value={rtwShareCode}
              onChange={(e) => setRtwShareCode(e.target.value)}
              placeholder="Enter your Right to Work share code"
              minLength={9}
              maxLength={9}
              required={!documentFile && !documentPreview}
            />
            <p className="text-sm text-gray-500">
              You can generate a share code at{" "}
              <a
                href="https://www.gov.uk/prove-right-to-work"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline"
              >
                gov.uk/prove-right-to-work
              </a>
            </p>
          </div>

          <div className="text-center text-gray-500 font-medium">OR</div>

          <div className="space-y-2">
            <Label htmlFor="document">Upload Passport/Birth Certificate</Label>
            <div
              className="border-2 border-dashed border-gray-300 rounded-md p-6 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
              onClick={() => triggerFileInput("document")}
            >
              {documentPreview ? (
                <div className="flex flex-col items-center space-y-4">
                  <DocumentPreview
                    src={documentPreview}
                    alt="Document preview"
                    file={documentFile}
                  />
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDocumentFile(null);
                        setDocumentPreview(null);
                      }}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <FileUp className="h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-sm text-gray-500 mb-2">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-400">
                    PNG, JPG or PDF (max. 10MB)
                  </p>
                </>
              )}
            </div>
            <Input
              id="document"
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept="image/png,image/jpeg,application/pdf"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-honeycomb hover:bg-amber-500 text-white"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Right to Work Information"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default RTWSection;
